import { expandEndpoint } from "@visioni/a3s-client-api";
import { ClientApiError, type ApiConfig } from "@visioni/a3s-client-api/app";
import type { TenantId } from "@visioni/a3s-schema";
import { ListVehiclesResponse, listVehiclesEndpoint, type FlatVehicleDetection } from "../client-messages/vehicles";
import { EXPORT_MAX_ROWS } from "../export/limits";
import type { VehicleFilters } from "../types";
import { filtersToApiQuery } from "./filter-params";

/**
 * Rows per request. The endpoint allows 500, but the proxy gives up on its call
 * to the vehicle tracker after 10 s, and a 500-row page takes about that long
 * (measured on Int, ~3 s for 200), so larger pages time out intermittently.
 */
const PAGE_SIZE = 200;

/**
 * Most pages fetched per export. Pages can come back short (the proxy drops
 * rows outside allocation windows), so this allows three times the pages full
 * ones would need while still bounding a run of near-empty pages.
 */
const MAX_PAGES = 3 * Math.ceil(EXPORT_MAX_ROWS / PAGE_SIZE);

export interface FetchAllVehiclesOptions {
  fetch: ApiConfig["authenticatedFetch"];
  tenant: { tenantId: TenantId; apiBaseUrl: string };
  filters: VehicleFilters;
  selectedSiteId: string | null;
  sort?: "asc" | "desc" | undefined;
  /** Aborts the in-flight request and stops paging; the call rejects with an `AbortError`. */
  signal?: AbortSignal;
  /** Called after each page with the number of rows fetched so far. */
  onProgress?: (fetched: number) => void;
}

export interface FetchAllVehiclesResult {
  vehicles: FlatVehicleDetection[];
  /** More sightings matched than the export row cap allowed. */
  truncated: boolean;
}

/**
 * Pages through the vehicles endpoint with the applied filters, up to
 * `EXPORT_MAX_ROWS` rows or `MAX_PAGES` pages. Reaching either limit is not a
 * failure: the result is marked `truncated` when sightings that matched were
 * left out.
 */
export async function fetchAllVehicles(options: FetchAllVehiclesOptions): Promise<FetchAllVehiclesResult> {
  const vehicles: FlatVehicleDetection[] = [];
  let cursor: string | undefined;
  let pages = 0;
  do {
    options.signal?.throwIfAborted();
    const page = await fetchPage(options, cursor);
    const room = EXPORT_MAX_ROWS - vehicles.length;
    vehicles.push(...page.vehicles.slice(0, room));
    options.onProgress?.(vehicles.length);
    cursor = page.nextCursor ?? undefined;
    if (vehicles.length >= EXPORT_MAX_ROWS) {
      return { vehicles, truncated: page.vehicles.length > room || cursor !== undefined };
    }
    if (++pages >= MAX_PAGES) return { vehicles, truncated: cursor !== undefined };
  } while (cursor !== undefined);
  return { vehicles, truncated: false };
}

/**
 * Waits before each retry of a page that failed with a gateway error (502,
 * 503, 504) or a network error: the proxy and the vehicle tracker time out now
 * and then under load, and a moment later the same page usually succeeds. Two
 * retries, then the failure stands. Refusals (other 4xx/5xx) and responses
 * that fail the schema are not retried, and cancelling stops the wait at once.
 */
const RETRY_DELAYS_MS = [1000, 2000];
const RETRYABLE_STATUSES = new Set([502, 503, 504]);

async function fetchPage(options: FetchAllVehiclesOptions, cursor: string | undefined): Promise<ListVehiclesResponse> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await requestPage(options, cursor);
    } catch (error) {
      const delay = RETRY_DELAYS_MS[attempt];
      if (delay === undefined || options.signal?.aborted || !isRetryable(error)) throw error;
      await wait(delay, options.signal);
    }
  }
}

function isRetryable(error: unknown): boolean {
  if (error instanceof ClientApiError) return RETRYABLE_STATUSES.has(error.status);
  // fetch rejects with a TypeError when the request never got a response.
  return error instanceof TypeError;
}

/** Resolves after `ms`, or rejects with an `AbortError` as soon as `signal` aborts. */
function wait(ms: number, signal: AbortSignal | undefined): Promise<void> {
  return new Promise((resolve, reject) => {
    const abort = () => {
      clearTimeout(timer);
      reject(new DOMException("The export was cancelled.", "AbortError"));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", abort);
      resolve();
    }, ms);
    if (signal?.aborted) abort();
    else signal?.addEventListener("abort", abort, { once: true });
  });
}

async function requestPage(
  options: FetchAllVehiclesOptions,
  cursor: string | undefined,
): Promise<ListVehiclesResponse> {
  const { fetch, tenant, filters, selectedSiteId, sort, signal } = options;
  const query = { ...filtersToApiQuery(filters, selectedSiteId, cursor, sort), limit: String(PAGE_SIZE) };
  const path = expandEndpoint(listVehiclesEndpoint, { path: { tenantId: tenant.tenantId }, query });
  const response = await fetch(`${tenant.apiBaseUrl}${path}`, signal ? { signal } : {});
  if (!response.ok) throw new ClientApiError(response.status, await errorMessage(response));
  const body: unknown = await response.json();
  return ListVehiclesResponse.parse(body);
}

/**
 * The body's `message` when the API sent JSON with one; otherwise a generic
 * message, so an HTML error page from a gateway is never shown to the user.
 */
async function errorMessage(response: Response): Promise<string> {
  const fallback = `Request failed: ${String(response.status)}`;
  try {
    const body: unknown = await response.json();
    return typeof body === "object" && body !== null && "message" in body && typeof body.message === "string" ?
        body.message
      : fallback;
  } catch {
    return fallback;
  }
}
